import React, { useEffect, useState } from 'react'
import { Bot, PhoneCall, Boxes, ShoppingBag, Radar, BrainCircuit } from 'lucide-react'
import Logo from './Logo'

/**
 * AuthGauge — a RADAR half-moon that IS the whole left auth panel (TEST: /signin).
 *
 * A THIN monochrome half-donut sits beside the brand mark; the logo pulses in the
 * hollow, and a SLOW, smooth radar beam (soft glow + long fading trail, one
 * direction — no clock-hand line, no back-and-forth) sweeps the band. As the beam
 * detects a service its icon lights, a blip pings, and that service's best outcome
 * blasts in beside it. Loops. `prefers-reduced-motion` guarded (static).
 */

const SERVICES = [
  { name: 'Support Chatbot',      best: 'Every visitor gets an instant answer', Icon: Bot },
  { name: 'Voice AI Receptionist', best: 'No call ever goes to voicemail',       Icon: PhoneCall },
  { name: 'Custom RAG API',        best: 'Answers cited to your own docs',        Icon: Boxes },
  { name: 'WhatsApp Commerce',     best: 'Shoppers check out inside the chat',    Icon: ShoppingBag },
  { name: 'SEO Intelligence',      best: 'Cited by ChatGPT & Perplexity',         Icon: Radar },
  { name: 'Marketing Brain',       best: 'You see why revenue moved',             Icon: BrainCircuit },
]

// Thin RIGHT half-donut; logo pulses in the (large) hollow centre C.
const CX = 220, CY = 360, Ri = 124, Ro = 184
const RM = (Ri + Ro) / 2
const N = SERVICES.length
const A0 = 72, A1 = -72
const ang = (i) => A0 - (i * (A0 - A1)) / (N - 1)
const rad = (d) => (d * Math.PI) / 180
const P = (r, d) => ({ x: CX + r * Math.cos(rad(d)), y: CY - r * Math.sin(rad(d)) })
const f = (n) => n.toFixed(1)

function ring(r, a = 90, b = -90) {
  const p0 = P(r, a), p1 = P(r, b)
  return `M ${f(p0.x)} ${f(p0.y)} A ${r} ${r} 0 0 1 ${f(p1.x)} ${f(p1.y)}`
}
function bandPath() {
  const o0 = P(Ro, 90), o1 = P(Ro, -90), i1 = P(Ri, -90), i0 = P(Ri, 90)
  return `M ${f(o0.x)} ${f(o0.y)} A ${Ro} ${Ro} 0 0 1 ${f(o1.x)} ${f(o1.y)} `
    + `L ${f(i1.x)} ${f(i1.y)} A ${Ri} ${Ri} 0 0 0 ${f(i0.x)} ${f(i0.y)} Z`
}
// Sweep sub-wedge from a0 to a1 (degrees) out to just past the band edge.
function wedge(a0, a1) {
  const o0 = P(Ro + 6, a0), o1 = P(Ro + 6, a1)
  const sweep = a0 > a1 ? 0 : 1
  return `M ${CX} ${CY} L ${f(o0.x)} ${f(o0.y)} A ${Ro + 6} ${Ro + 6} 0 0 ${sweep} ${f(o1.x)} ${f(o1.y)} Z`
}

const SPEED = 0.55, TICK = 32   // slow, smooth, one direction

export default function AuthGauge() {
  const [beam, setBeam] = useState(90)
  const [ping, setPing] = useState(0)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced) { setBeam(ang(0)); setPing(0); return }

    let a = 92, cur = 0
    const id = setInterval(() => {
      a -= SPEED
      if (a < -96) a = 96                 // reset off the service range (ends are empty)
      setBeam(a)
      let bi = -1, bd = 9
      for (let i = 0; i < N; i++) { const dd = Math.abs(a - ang(i)); if (dd < bd) { bd = dd; bi = i } }
      if (bi >= 0 && bi !== cur) { cur = bi; setPing(bi) }
    }, TICK)
    return () => clearInterval(id)
  }, [])

  const svc = SERVICES[ping]
  const lp = P(Ro + 16, ang(ping))

  return (
    <div className="rrauth__gauge" role="img" aria-label={`ReRight services radar — ${svc.name}: ${svc.best}`}>
      <svg className="rrauth__gauge-svg" viewBox="92 128 590 468" width="590" height="468" aria-hidden="true">
        <defs>
          <radialGradient id="radarFill" cx={CX} cy={CY} r={Ro} gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.05" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0.012" />
          </radialGradient>
        </defs>

        {/* the thin band */}
        <path d={bandPath()} fill="url(#radarFill)" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" />

        {/* radar grid — a mid arc + radial spokes at each service */}
        <path d={ring(RM)} className="radar-ring" />
        {SERVICES.map((s, i) => {
          const a = P(Ri, ang(i)), b = P(Ro, ang(i))
          return <line key={s.name} x1={f(a.x)} y1={f(a.y)} x2={f(b.x)} y2={f(b.y)} className="radar-spoke" />
        })}

        {/* sweep — soft glow with a long fading trail (no hard needle line).
            Clamp to the band range so the trail never bleeds past the top/bottom. */}
        <g className="radar-sweep">
          {[[0, 5, 0.26], [5, 18, 0.15], [18, 40, 0.07], [40, 72, 0.025]].map(([s, e, op], k) => {
            const a0 = Math.max(-94, Math.min(94, beam + s))
            const a1 = Math.max(-94, Math.min(94, beam + e))
            if (a1 - a0 < 0.5) return null
            return <path key={k} d={wedge(a0, a1)} fill="var(--accent)" opacity={op} />
          })}
        </g>

        {/* service icons — dim, the detected one lights + pings */}
        {SERVICES.map((s, i) => {
          const c = P(RM, ang(i))
          const on = i === ping
          const IconC = s.Icon
          return (
            <g key={s.name}>
              {on && <circle cx={c.x} cy={c.y} r="16" className="radar-blip" />}
              <foreignObject x={c.x - 14} y={c.y - 14} width="28" height="28">
                <div xmlns="http://www.w3.org/1999/xhtml" className={`radar-ico ${on ? 'is-on' : ''}`}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28 }}>
                  <IconC size={20} strokeWidth={2} />
                </div>
              </foreignObject>
            </g>
          )
        })}

        {/* the detected service's best outcome, blasting in beside it */}
        <foreignObject x={f(lp.x)} y={f(lp.y - 30)} width="250" height="62">
          <div xmlns="http://www.w3.org/1999/xhtml" className="radar-label" key={ping}>
            <span className="radar-label-svc">{svc.name}</span>
            <span className="radar-label-best">{svc.best}</span>
          </div>
        </foreignObject>

        {/* the logo circle in the hollow — pulsing like a heartbeat (lub-dub, rest).
            A synced ripple expands on the beat; the disc + logo scale together. */}
        <circle cx={CX} cy={CY} r="62" className="radar-heart-ripple" />
        <g className="radar-heart">
          <circle cx={CX} cy={CY} r="62" className="radar-face" />
          <foreignObject x={CX - 66} y={CY - 36} width="132" height="72">
            <a xmlns="http://www.w3.org/1999/xhtml" href="/home" aria-label="ReRight home"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 132, height: 72, cursor: 'pointer' }}>
              <Logo size="md" tone="light" showText={false} />
            </a>
          </foreignObject>
        </g>
      </svg>
    </div>
  )
}
